// 1. React imports
import { useEffect, useRef } from 'react';

// 3. Internal components
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { WizardActions } from '~/components/onboarding/WizardActions';
import { Field, FieldError, FieldLabel } from '~/components/ui/field';
import { Input } from '~/components/ui/input';

// 4. Type imports
import type { WizardUseCase } from '~/types/onboarding';

// 5. Hooks and utilities
import { getTopicNameSuggestion } from '~/utils/onboardingHelpers';

/**
 * Props for TopicCreationStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface TopicCreationStepProps {
  selectedUseCase: WizardUseCase | null;
  topicName: string;
  onTopicNameChange: (name: string) => void;
  onCreateTopic: () => Promise<void>;
  isCreating: boolean;
  error: string | null;
  onBack: () => void;
  onSkip: () => void;
}

/**
 * Topic creation step for onboarding wizard.
 *
 * Pre-fills TextField with suggested topic name based on selected use case.
 * Displays inline validation errors and CircularProgress during creation.
 * Calls onCreateTopic on Next button click.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Reference: OnboardingQuestionsForm for form pattern with error handling.
 *
 * @example
 * ```tsx
 * <TopicCreationStep
 *   selectedUseCase="agent_communications"
 *   topicName={topicName}
 *   onTopicNameChange={setTopicName}
 *   onCreateTopic={handleCreateTopic}
 *   isCreating={false}
 *   error={null}
 *   onBack={() => setStep(1)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function TopicCreationStep({
  selectedUseCase,
  topicName,
  onTopicNameChange,
  onCreateTopic,
  isCreating,
  error,
  onBack,
  onSkip,
}: TopicCreationStepProps) {
  // Pre-fill topic name with a suggestion only once, on mount. Re-running this on
  // every `topicName` change (the prior dependency) meant the field re-filled
  // itself the moment the user cleared it, making it impossible to leave blank
  // while typing a replacement.
  const hasPrefilledRef = useRef(false);
  useEffect(() => {
    if (hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;
    if (!topicName) {
      const suggestion = getTopicNameSuggestion(selectedUseCase);
      onTopicNameChange(suggestion);
    }
    // oxlint-disable-next-line react/exhaustive-deps -- intentionally mount-only
  }, []);

  // Event handler with handle prefix
  const handleNextClick = () => {
    void onCreateTopic();
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleNextClick();
  };

  // Validation: topic name required and must be non-empty
  const isTopicNameValid = topicName.trim().length > 0;
  const showEmptyError = !isTopicNameValid && topicName.length > 0;

  return (
    <form onSubmit={handleFormSubmit}>
      <p className="mb-8 text-sm text-muted-foreground">
        Topics organize your messages. Choose a descriptive name for your first topic.
      </p>

      {/* Error Alert */}
      {error && (
        <div className="mb-6">
          <ErrorAlert message={error} />
        </div>
      )}

      {/* Topic Name Input */}
      <Field data-invalid={showEmptyError} className="mb-6">
        <FieldLabel htmlFor="topic-name">Topic Name</FieldLabel>
        <Input
          id="topic-name"
          value={topicName}
          onChange={(event) => onTopicNameChange(event.target.value)}
          disabled={isCreating}
          aria-invalid={showEmptyError}
        />
        {showEmptyError ? (
          <FieldError>Topic name cannot be empty</FieldError>
        ) : (
          <p className="text-sm text-muted-foreground">
            Example: Agent Messages, Team Alerts, Customer Notifications
          </p>
        )}
      </Field>

      {/* Wizard Actions */}
      <WizardActions
        onBack={onBack}
        onNext={handleNextClick}
        onSkip={onSkip}
        nextLabel="Create Topic"
        nextDisabled={!isTopicNameValid}
        showBack={true}
        isLoading={isCreating}
      />
    </form>
  );
}
